import { useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { prs as prsApi } from "@/lib/api";
import { toast } from "sonner";

/** Compact PR # search box. Type a Voc_no, hit Enter.
 * Calls onFound(vocNo) if the PR exists (after a backend check),
 * or fires an error toast if not. */
export default function PrSearchBox({ onFound, placeholder = "Find PR #…", testid = "pr-search-box", variant = "header" }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const v = q.trim();
    if (!v) return;
    if (!/^\d+$/.test(v)) {
      toast.error("Enter a numeric PR number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await prsApi.get(v);
      if (data?.voc_no) {
        onFound(Number(v), data);
        setQ("");
      } else {
        toast.error(`PR #${v} not found`);
      }
    } catch (err) {
      const msg = err?.response?.status === 404 ? `PR #${v} not found` : (err?.response?.data?.detail || "Lookup failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const dark = variant === "header";
  return (
    <form onSubmit={submit} className="relative" data-testid={testid}>
      {busy
        ? <Loader2 size={13} className={`absolute left-2.5 top-2 animate-spin ${dark ? "text-cyan-100" : "text-slate-400"}`} />
        : <Search size={13} className={`absolute left-2.5 top-2 ${dark ? "text-cyan-100" : "text-slate-400"}`} />
      }
      <input
        type="search"
        inputMode="numeric"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testid}-input`}
        className={`pl-7 pr-2 h-7 rounded-md text-xs w-24 sm:w-40 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition-all ${
          dark
            ? "bg-white/10 text-white placeholder-cyan-200 focus:bg-white/20"
            : "bg-slate-100 text-slate-800 placeholder-slate-400 focus:bg-white border border-slate-200"
        }`}
      />
    </form>
  );
}
